import { Button, Center, Group, Loader, Modal, Stack, Text, TextInput } from "@mantine/core";
import dayjs from "dayjs";
import { CalendarBooking, CalendarEvent, PublicSchedule } from "../../utils/schedule";
import { useForm } from "@mantine/form";
import API from "../../utils/const";
import { authFetch } from "../../utils/session";
import { withLoading } from "../../utils/common";
import { useState } from "react";
import { handleApiError } from "../../utils/app";
import { useTranslation } from "react-i18next";

export const ScheduleConfirmModal = ({ dateTime, schedule, opened, onClose, onConfirm, onPresubmit }: { dateTime: Date | null, schedule: PublicSchedule | null, opened: boolean, onClose: () => void, onConfirm: (bookingId: string) => void, onPresubmit: null | (() => void) }) => {
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation(['app']);
    const keepQueryParams = Array.from(
        new URLSearchParams(window.location.search).entries()
    ).filter(([key, value]) => key.startsWith('utm_') || key.startsWith('calensync_')).reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
    }, {} as Record<string, string>);

    const form = useForm({
        initialValues: {
            name: '',
            email: '',
            phone: '',
            message: '',
        },
        validate: {
            name: (value) => value.length < 2 ? 'Name must be at least 2 characters' : null,
            email: (value) => !value.includes('@') ? 'Invalid email' : null,
        },
    });

    if (dateTime === null || schedule === null) {
        return null;
    }
    const startObj = dayjs(dateTime);
    const startHour = startObj.hour();
    const startMinute = startObj.minute();

    const endObj = startObj.add(schedule.max_event_length, 'minute');
    const endHour = endObj.hour();
    const endMinute = endObj.minute();

    const onSubmit = withLoading(setIsLoading)(async (values: { name: string, email: string, phone: string, message: string }) => {
        if(typeof onPresubmit === 'function'){
            onPresubmit();
            return;
        }
        const payload = {
            ...values,
            start: startObj.toISOString(),
            query_params: keepQueryParams,
        }
        const response = await authFetch(`${API}/schedule/${schedule.uuid}/book`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        })
        if (!response.ok) {
            handleApiError(response, t, true);
            return 
        }
        const bookingId = (await response.json()).uuid;
        onConfirm(bookingId);
        onClose();
    })

    return <Modal opened={opened} onClose={onClose} title={t('schedule.confirm.title')}>
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack gap="md">
                <Stack gap="2">
                    <Text>
                        {t('schedule.confirm.date')}: <Text span fw={700}>{startObj.toDate().toLocaleDateString()}</Text>
                    </Text>
                    <Text>
                        {t('schedule.confirm.time')}: <Text span fw={700}>{startHour.toString().padStart(2, '0')}:{startMinute.toString().padStart(2, '0')} - {endHour.toString().padStart(2, '0')}:{(endMinute).toString().padStart(2, '0')}</Text>
                    </Text>
                </Stack>
                <Stack gap="2">
                    <TextInput 
                        label={t('schedule.confirm.name')} 
                        placeholder={t('schedule.confirm.name-placeholder')} 
                        {...form.getInputProps('name')} 
                    />
                    <TextInput 
                        label={t('schedule.confirm.email')} 
                        placeholder={t('schedule.confirm.email-placeholder')} 
                        {...form.getInputProps('email')} 
                    />
                    <TextInput 
                        label={t('schedule.confirm.phone')} 
                        placeholder={t('schedule.confirm.phone-placeholder')} 
                        {...form.getInputProps('phone')} 
                    />
                    <TextInput 
                        label={t('schedule.confirm.message')} 
                        placeholder={t('schedule.confirm.message-placeholder')} 
                        {...form.getInputProps('message')} 
                    />
                </Stack>
                <Text c='dimmed' size='sm'>🔐 Your data is encrypted and stored securely. We will not share it with anyone except for the host.</Text>

                <Group justify="flex-end">
                    <Button type="submit" loading={isLoading}>Confirm</Button>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                </Group>
            </Stack>
        </form>
    </Modal>;
}

export const ScheduleUpdateModal = ({ 
    dateTime, 
    schedule, 
    opened, 
    onClose, 
    onConfirm,
    currentBookingUuid 
}: { 
    dateTime: Date | null, 
    schedule: PublicSchedule | null, 
    opened: boolean, 
    onClose: () => void, 
    onConfirm: (bookingId: string) => void,
    currentBookingUuid: string | null
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation(['app']);

    if (dateTime === null || schedule === null || currentBookingUuid === null) {
        return null;
    }
    const startObj = dayjs(dateTime);
    const starthour = startObj.hour();
    const startMinute = startObj.minute();
    
    const endObj = startObj.add(schedule.max_event_length, 'minute');
    const endHour = endObj.hour();
    const endMinute = endObj.minute();


    const onSubmit = withLoading(setIsLoading)(async () => {
        const payload = {
            start: startObj.toISOString(),
        }
        const response = await authFetch(`${API}/booking/${currentBookingUuid}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        })
        if (!response.ok) {
            handleApiError(response, t, true);
            return 
        }
        onConfirm(currentBookingUuid);
        onClose();
    })

    return <Modal opened={opened} onClose={onClose} title="Update Appointment">
        <Stack gap="md">
            <Stack gap="2">
                <Text>New Date: <Text span fw={700}>{startObj.toDate().toLocaleDateString()}</Text></Text>
                <Text>New Time: <Text span fw={700}>{starthour.toString().padStart(2, '0')}:{startMinute.toString().padStart(2, '0')} - {endHour.toString().padStart(2, '0')}:{(endMinute).toString().padStart(2, '0')}</Text></Text>
            </Stack>
            
            <Text c='dimmed' size='sm'>Are you sure you want to reschedule this appointment?</Text>

            <Group justify="flex-end">
                <Button onClick={onSubmit} loading={isLoading}>Update</Button>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
            </Group>
        </Stack>
    </Modal>;
}